import { createAction, handleActions } from "redux-actions";
import {
    getAssignableStoragesAPI,
    getAssignedStoragesAPI,
    updateAssignedStoragesAPI,
} from "@modules/apis/project-storages";
import { PROJECT_STORAGES_ACTION_TYPE } from "@constants";

const initialState = {
    pending: false,
    data: null,
    error: null,
};

const GET_PROJECT_STORAGES_PENDING = "GET_PROJECT_STORAGES_PENDING";
const GET_PROJECT_STORAGES_SUCCESS = "GET_PROJECT_STORAGES_SUCCESS";
const GET_PROJECT_STORAGES_FAILURE = "GET_PROJECT_STORAGES_FAILURE";
const PROJECT_STORAGES_RESET = "PROJECT_STORAGES_RESET";

export default handleActions(
    {
        [GET_PROJECT_STORAGES_PENDING]: (state) => {
            return {
                ...state,
                pending: true,
            };
        },
        [GET_PROJECT_STORAGES_SUCCESS]: (state, action) => {
            return {
                ...state,
                pending: false,
                data: action.payload,
            };
        },
        [GET_PROJECT_STORAGES_FAILURE]: (state, action) => {
            return {
                ...state,
                pending: false,
                error: action.payload,
            };
        },
        [PROJECT_STORAGES_RESET]: () => {
            return {
                ...initialState,
            };
        },
    },
    initialState,
);

export const getProjectStorages =
    ({ isProjectSetting, projectId, signal }) =>
    async (dispatch, getState) => {
        const { stage } = getState();
        try {
            dispatch({
                type: GET_PROJECT_STORAGES_PENDING,
            });
            let assignableStorageResponse = null;
            if (!isProjectSetting) {
                assignableStorageResponse = await getAssignableStoragesAPI(stage.id, stage.endpoint, projectId, signal);
            }

            let assignedStorageResponse = { data: { results: [], nextToken: null } };
            let params = { isDirectlyAccessible: true };
            do {
                if (assignedStorageResponse.data.nextToken) params.nextToken = assignedStorageResponse.data.nextToken;
                const results = await getAssignedStoragesAPI(stage.id, stage.endpoint, projectId, params);
                assignedStorageResponse.data = {
                    results: assignedStorageResponse.data.results.concat(
                        results.data.results?.map((el) => ({ ...el, isAssigned: true })),
                    ),
                    nextToken: results.data.nextToken ?? null,
                };
            } while (assignedStorageResponse.data.nextToken !== null);

            const storages = assignedStorageResponse.data.results.concat(
                assignableStorageResponse?.data?.results?.map((el) => ({
                    ...el,
                    isAssigned: false,
                    isDownloadable: false,
                    isUploadable: false,
                })) ?? [],
            );
            dispatch({
                type: GET_PROJECT_STORAGES_SUCCESS,
                payload: { projectId, storages },
            });

            return storages;
        } catch (error) {
            if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
                console.log("getProjectStorages request was cancelled");
                return null;
            }
            dispatch({
                type: GET_PROJECT_STORAGES_FAILURE,
                payload: error,
            });
        }
    };

export const updateProjectStorages =
    ({ data }) =>
    async (dispatch, getState) => {
        const { stage } = getState();
        const getParsedValues = (action, values) => {
            let data = {};
            if (action === PROJECT_STORAGES_ACTION_TYPE.BULK_ADD) {
                data = {
                    storages: values.map(({ name, isUploadable, isDownloadable }) => ({
                        name,
                        isUploadable,
                        isDownloadable,
                    })),
                };
            } else if (action === PROJECT_STORAGES_ACTION_TYPE.BULK_REMOVE) {
                data = { ids: values.map(({ id }) => id) };
            } else if (action === PROJECT_STORAGES_ACTION_TYPE.BULK_UPDATE) {
                data = {
                    storages: values.map(({ id, isUploadable, isDownloadable }) => ({
                        id,
                        isUploadable,
                        isDownloadable,
                    })),
                };
            }
            return {
                action,
                data,
            };
        };

        const projectStoragesData = data.flatMap((el) => {
            const addedStorages = el.storages.filter((s) => s.action === PROJECT_STORAGES_ACTION_TYPE.BULK_ADD);
            const removedStorages = el.storages.filter((s) => s.action === PROJECT_STORAGES_ACTION_TYPE.BULK_REMOVE);
            const updatedStorages = el.storages.filter((s) => s.action === PROJECT_STORAGES_ACTION_TYPE.BULK_UPDATE);

            const operations = [];

            if (addedStorages.length > 0) {
                operations.push({
                    action: PROJECT_STORAGES_ACTION_TYPE.BULK_ADD,
                    data: {
                        projectId: el.projectId,
                        params: { ...getParsedValues(PROJECT_STORAGES_ACTION_TYPE.BULK_ADD, addedStorages) },
                    },
                });
            }

            if (removedStorages.length > 0) {
                operations.push({
                    action: PROJECT_STORAGES_ACTION_TYPE.BULK_REMOVE,
                    data: {
                        projectId: el.projectId,
                        params: { ...getParsedValues(PROJECT_STORAGES_ACTION_TYPE.BULK_REMOVE, removedStorages) },
                    },
                });
            }

            if (updatedStorages.length > 0) {
                operations.push({
                    action: PROJECT_STORAGES_ACTION_TYPE.BULK_UPDATE,
                    data: {
                        projectId: el.projectId,
                        params: { ...getParsedValues(PROJECT_STORAGES_ACTION_TYPE.BULK_UPDATE, updatedStorages) },
                    },
                });
            }

            return operations;
        });

        const response = await Promise.allSettled(
            projectStoragesData.map(async ({ action, data }) => {
                try {
                    return await updateAssignedStoragesAPI(stage.id, stage.endpoint, data);
                } catch (error) {
                    error.projectId = data.projectId;
                    error.action = action;
                    throw error;
                }
            }),
        );

        const failedOperations = response
            .map((result) => {
                if (result.status === "rejected") {
                    return result.reason;
                }
                return null;
            })
            .filter(Boolean);

        if (failedOperations.length > 0) {
            const errorMessage = `Failed to update project storages. ${
                failedOperations.length
            } operation(s) failed: ${failedOperations
                .map((op) => `Project ${op.projectId} (${op.actionType})`)
                .join(", ")}`;
            const error = new Error(errorMessage);
            error.data = failedOperations;
            throw error;
        }
        return response;
    };

export const projectStoragesReset = createAction(PROJECT_STORAGES_RESET);
